import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaHeart, FaRegHeart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { menuService, getPublicSelectedLanguages, schoolService, API_BASE_URL } from '../utils/api';
import { useSchool } from '../contexts/SchoolContext';
import LanguageSelector from '../components/LanguageSelector';
import MenuTypeSelector from '../components/MenuTypeSelector';
import languageService from '../utils/languageService';
import { useDispatch } from 'react-redux';
import { getPublicUrl } from '../utils/nhost';
import { useAvailableMenuTypes, usePublicMenu, usePublicWeekMenu } from '../hooks/useMenu';

const PublicMenuViewer = () => {
  const { t, i18n } = useTranslation();
  const { school, loading: schoolLoading } = useSchool();
  const dispatch = useDispatch();
  
  console.log(`[DEBUG-MenuViewer] PublicMenuViewer rendering for school: ${school?.name}, subdomain: ${school?.subdomain} at ${new Date().toISOString()}`);
  
  // Get available menu types using React Query hook
  const { 
    data: availableTypes = [], 
    isLoading: isLoadingMenuTypes,
    error: menuTypesError,
    dataUpdatedAt: menuTypesUpdatedAt,
    refetch: refetchMenuTypes
  } = useAvailableMenuTypes(school?.subdomain);
  
  // Force a refetch of menu types when component mounts
  useEffect(() => {
    if (school?.subdomain) {
      // Force immediate refetch on mount
      console.log(`[DEBUG-MenuViewer] Force refetching menu types on mount at ${new Date().toISOString()}`);
      refetchMenuTypes();
    }
  }, [school, refetchMenuTypes]);
  
  // Log whenever availableTypes changes
  useEffect(() => {
    console.log(`[DEBUG-MenuViewer] availableTypes updated at ${new Date().toISOString()}`);
    console.log(`[DEBUG-MenuViewer] availableTypes data:`, availableTypes);
    console.log(`[DEBUG-MenuViewer] Last data fetch: ${new Date(menuTypesUpdatedAt).toISOString()}`);
    
    // Check if a specific menu type has been removed (for debugging)
    const localStorageMenuTypeId = localStorage.getItem(`selectedMenuType_${school?.subdomain}`);
    if (localStorageMenuTypeId) {
      const menuTypeId = parseInt(localStorageMenuTypeId, 10);
      const exists = availableTypes.some(mt => mt.id === menuTypeId);
      console.log(`[DEBUG-MenuViewer] Checking if menu type ID ${menuTypeId} exists: ${exists}`);
    }
  }, [availableTypes, menuTypesUpdatedAt, school]);
  
  // We no longer allow custom month/year selection - always use current month
  const [menu, setMenu] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [menuTypes, setMenuTypes] = useState([]);
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [courses, setCourses] = useState([]);
  const [menuTypeCourses, setMenuTypeCourses] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);
  const [menuError, setMenuError] = useState(null);
  const [checkingMenuTypes, setCheckingMenuTypes] = useState(false);
  const [languageChecked, setLanguageChecked] = useState(false);
  
  // State for favorite menu types
  const [favoriteMenuTypeIds, setFavoriteMenuTypeIds] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0); // For favorite carousel
  
  // State for mobile day view
  const [selectedDay, setSelectedDay] = useState(new Date().getDate()); // Default to current day number
  const [validDays, setValidDays] = useState([]); // Store available days with data

  // Ref to track initial mount/load
  const isInitialMount = useRef(true);
  // Ref to track if initial carousel index has been set based on loaded state
  const isInitialCarouselSet = useRef(false);

  // The month/week being viewed (may differ from current)
  const [viewMonth, setViewMonth] = useState(null);
  const [viewYear, setViewYear] = useState(null);   // The year being viewed
  const [viewWeek, setViewWeek] = useState(null);   // The week being viewed (for weekly display)
  const [loadingPeriodData, setLoadingPeriodData] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState([]); // Array of available months/weeks
  const [isWeeklyView, setIsWeeklyView] = useState(false); // Flag indicating weekly/monthly view

  // State for showing/hiding day notes
  const [showNotes, setShowNotes] = useState(true);
  
  // Create a memoized helper to get the selected menu type object
  const selectedMenuType = useMemo(() => {
    if (!selectedMenuTypeId || !availableTypes || availableTypes.length === 0) return null;
    return availableTypes.find(mt => mt.id === parseInt(selectedMenuTypeId)) || null;
  }, [selectedMenuTypeId, availableTypes]);
  
  // Get localStorage key for favorites based on school subdomain
  const getFavoritesStorageKey = useCallback(() => {
    return school?.subdomain ? `favoriteMenuTypes_${school.subdomain}` : null;
  }, [school?.subdomain]);

  // Load favorite menu types from localStorage on mount or school change
  useEffect(() => {
    console.log('[DEBUG-LoadFavorites] Load effect running...', { schoolLoading, schoolSubdomain: school?.subdomain });
    if (!school || schoolLoading) {
      console.log('[DEBUG-LoadFavorites] Exiting load effect: School not ready.');
      return;
    }

    const storageKey = getFavoritesStorageKey();
    console.log(`[DEBUG-LoadFavorites] Storage key: ${storageKey}`);
    if (!storageKey) {
      console.log('[DEBUG-LoadFavorites] Exiting load effect: No storage key.');
      return;
    }

    const storedFavoritesRaw = localStorage.getItem(storageKey);
    console.log(`[DEBUG-LoadFavorites] Raw value from localStorage ('${storageKey}'):`, storedFavoritesRaw);

    if (storedFavoritesRaw) {
      try {
        const parsedFavorites = JSON.parse(storedFavoritesRaw);
        console.log('[DEBUG-LoadFavorites] Parsed favorites:', parsedFavorites);
        if (Array.isArray(parsedFavorites)) {
          // We still filter later based on availableTypes, but load the raw array now
          setFavoriteMenuTypeIds(parsedFavorites);
          console.log('[DEBUG-LoadFavorites] Setting favoriteMenuTypeIds state with loaded data.');
        } else {
          console.warn('[DEBUG-LoadFavorites] Parsed data is not an array, resetting.');
          setFavoriteMenuTypeIds([]);
        }
      } catch (e) {
        console.error("[DEBUG-LoadFavorites] Failed to parse favorites from localStorage", e);
        setFavoriteMenuTypeIds([]); // Reset if parsing fails
      }
    } else {
       console.log('[DEBUG-LoadFavorites] No favorites found in localStorage, ensuring state is empty array.');
       setFavoriteMenuTypeIds([]); // Ensure it's an empty array if nothing is stored
    }
     // Reset carousel index when school changes
     setCarouselIndex(0); 

     // Mark initial load as complete AFTER attempting to set state
     // Use a timeout to ensure it runs after the current render cycle potentially finishes
     // Note: This timeout isn't strictly necessary if the save effect dependency array is correct,
     // but adds a safety layer against potential race conditions in state setting vs effect running.
     const timer = setTimeout(() => {
        if (isInitialMount.current) {
            console.log('[DEBUG-LoadFavorites] Marking initial mount as complete.');
            isInitialMount.current = false;
        }
     }, 0);

     return () => clearTimeout(timer); // Cleanup timer on unmount or re-run

  }, [school, schoolLoading, getFavoritesStorageKey]);

  // Calculate actual favorite menu types (filtering based on availableTypes)
  const actualFavoriteMenuTypes = availableTypes.filter(mt => 
    favoriteMenuTypeIds.includes(mt.id)
  );

  // Save favorite menu types to localStorage whenever they change
  useEffect(() => {
    // Skip saving during initial mount/load until the load effect marks it complete
    if (isInitialMount.current) {
        console.log('[DEBUG-SaveFavorites] Skipping save: Initial mount/load not complete.');
        return; 
    }

    console.log('[DEBUG-SaveFavorites] Save effect running...');
    const storageKey = getFavoritesStorageKey();
    console.log(`[DEBUG-SaveFavorites] Storage key: ${storageKey}`);

    // Only save if we have a key and school is loaded/available
    if (storageKey && !schoolLoading && school) { 
      const dataToSave = JSON.stringify(favoriteMenuTypeIds);
      console.log(`[DEBUG-SaveFavorites] Saving favorites to localStorage ('${storageKey}'):`, favoriteMenuTypeIds);
      console.log(`[DEBUG-SaveFavorites] Stringified data:`, dataToSave);
      localStorage.setItem(storageKey, dataToSave);
    } else {
      console.log('[DEBUG-SaveFavorites] Skipping save: Storage key or school not ready.', { storageKey, schoolLoading, schoolExists: !!school });
    }
  }, [favoriteMenuTypeIds, getFavoritesStorageKey, schoolLoading, school]); // Dependencies remain the same

  // Effect to set the initial carousel index after load
  useEffect(() => {
    // Only run once after initial load and state determination
    if (isInitialMount.current || isInitialCarouselSet.current || !selectedMenuTypeId || actualFavoriteMenuTypes.length === 0) {
      return;
    }

    const isSelectedFavorite = actualFavoriteMenuTypes.some(mt => mt.id === selectedMenuTypeId);
    const isCarouselActive = actualFavoriteMenuTypes.length >= 4;

    if (isSelectedFavorite && isCarouselActive) {
      const favIndex = actualFavoriteMenuTypes.findIndex(mt => mt.id === selectedMenuTypeId);
      if (favIndex !== -1) {
          let targetIndex = 0;
          // If the favorite is not the first or second item, adjust the index
          if (favIndex > 1) { 
             targetIndex = favIndex - 1; // Show it as the second item
          }
          // Else, keep targetIndex at 0 (it's already visible)
          
          if (targetIndex !== carouselIndex) {
             console.log(`[DEBUG-CarouselInit] Adjusting initial carousel index to ${targetIndex} for selected favorite ID ${selectedMenuTypeId}`);
             setCarouselIndex(targetIndex);
          }
      }
    }
    
    // Mark this effect as run
    isInitialCarouselSet.current = true;
    console.log('[DEBUG-CarouselInit] Initial carousel position check complete.');

  }, [selectedMenuTypeId, actualFavoriteMenuTypes]); // Runs when selection or favorites list changes

  // Debug hook to inspect weekly menu data structure
  useEffect(() => {
    if (menu && isWeeklyView) {
      console.log('[DEBUG] Weekly menu data:', menu);
      console.log('[DEBUG] Menu tags:', menu.tags);
      
      if (menu.days && menu.days.length > 0) {
        const firstDay = menu.days[0];
        if (firstDay.courses && firstDay.courses.length > 0) {
          const firstCourse = firstDay.courses[0];
          if (firstCourse.dishes && firstCourse.dishes.length > 0) {
            console.log('[DEBUG] First dish tags:', firstCourse.dishes[0].tags);
          }
        }
      }
    }
  }, [menu, isWeeklyView]);

  // Check and handle language support for this school
  useEffect(() => {
    const checkLanguageSupport = async () => {
      if (schoolLoading || !school || languageChecked) return;
      
      try {
        // Get the user's preferred language
        const userPreferredLanguage = languageService.getPreferredLanguage();
        console.log('[PublicMenuViewer] User preferred language:', userPreferredLanguage);
        
        // Get selected languages for the school - using public endpoint
        const languagesData = await getPublicSelectedLanguages(school.id);
        console.log('[PublicMenuViewer] School supported languages:', languagesData.map(l => l.code));
        
        // Check if user's preferred language is supported by this school
        const isUserLanguageSupported = languagesData.some(
          lang => lang.code === userPreferredLanguage
        );
        
        if (isUserLanguageSupported) {
          // If user's preferred language is supported, make sure it's active
          console.log('[PublicMenuViewer] User preferred language is supported by this school');
          if (userPreferredLanguage !== i18n.language) {
            console.log('[PublicMenuViewer] Switching to user preferred language:', userPreferredLanguage);
            await languageService.changeLanguage(userPreferredLanguage, dispatch, false);
          }
        } else {
          // If user's preferred language is not supported, temporarily switch to school's default
          console.log('[PublicMenuViewer] User preferred language is NOT supported by this school');
          
          let defaultLang = null;
          // Get the default language ID directly from the school object
          const defaultLangId = school?.default_language_id;
          
          if (defaultLangId) {
            // Find the language object using the ID from the already fetched languagesData
            defaultLang = languagesData.find(lang => lang.id === defaultLangId);
            console.log('[PublicMenuViewer] Found school default language from context:', defaultLang);
          } else {
            console.log('[PublicMenuViewer] School default language ID not found in context.');
          }
          
          if (defaultLang && defaultLang.code !== i18n.language) {
            console.log('[PublicMenuViewer] Temporarily switching to school default language:', defaultLang.code);
            // Use temporary switch to avoid saving as preference
            await languageService.temporarilySwitchLanguage(defaultLang.code, dispatch);
          } else if (languagesData.length > 0) {
            // Fallback to first available language if no default is set
            console.log('[PublicMenuViewer] Temporarily switching to first available language:', languagesData[0].code);
            await languageService.temporarilySwitchLanguage(languagesData[0].code, dispatch);
          }
        }
        
        setLanguageChecked(true);
      } catch (error) {
        console.error('[PublicMenuViewer] Error checking language support:', error);
      }
    };
    
    checkLanguageSupport();
  }, [school, schoolLoading, dispatch, i18n.language, languageChecked]);

  // Group days by week for calendar display & get valid days for mobile selector
  useEffect(() => {
    console.log('[PublicViewer-Debug] useEffect for day processing triggered. Menu:', menu);
    if (!menu || !menu.days) {
      console.log('[PublicViewer-Debug] Day processing effect: No menu or menu.days, returning.');
      setValidDays([]); // Ensure validDays is cleared if there's no menu
      setDays([]);      // Ensure days for desktop view are cleared
      setCourses([]);   // Ensure courses are cleared
      return;
    }
    
    if (isWeeklyView) {
      console.log('[PublicViewer-Debug] Processing days for WEEKLY view');
      const sortedDays = [...menu.days].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
      const weekdaysOnly = sortedDays.filter(day => {
        const date = new Date(day.date);
        const dayOfWeek = date.getDay();
        return dayOfWeek >= 1 && dayOfWeek <= 5;
      });
      setValidDays(weekdaysOnly);

      let newSelectedDayIndex = selectedDay; // Preserve current index if valid

      if (weekdaysOnly.length > 0) {
        // Check if the current selectedDay (index) is still valid
        if (newSelectedDayIndex === null || newSelectedDayIndex < 0 || newSelectedDayIndex >= weekdaysOnly.length) {
          const today = new Date();
          const todayISO = today.toISOString().split('T')[0];
          const todayIndex = weekdaysOnly.findIndex(day => day.date === todayISO);
          if (todayIndex !== -1) {
            newSelectedDayIndex = todayIndex;
          } else {
            const futureIndex = weekdaysOnly.findIndex(day => day.date >= todayISO);
            newSelectedDayIndex = futureIndex !== -1 ? futureIndex : weekdaysOnly.length - 1;
          }
          console.log(`[PublicViewer-Debug] Weekly view: Resetting selectedDay index to ${newSelectedDayIndex}`);
          setSelectedDay(newSelectedDayIndex);
        }
      } else {
        if (selectedDay !== null) {
          setSelectedDay(null);
          console.log('[PublicViewer-Debug] Weekly view: No valid weekdays, setting selectedDay to null.');
        }
      }
      
      const week = Array(5).fill(null);
      weekdaysOnly.forEach(day => {
        const date = new Date(day.date);
        const dayOfWeek = date.getDay();
        week[dayOfWeek - 1] = day;
      });
      setDays([week]);
      
      const weekdayNames = Array.from({ length: 5 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - date.getDay() + i + 1);
        const dayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
        return dayName.charAt(0).toUpperCase() + dayName.slice(1);
      });
      setWeekDays(weekdayNames);
      
      if (weekdaysOnly.length > 0) {
        const menuCoursesSet = new Set();
        weekdaysOnly.forEach(day => {
          if (day.courses) {
            day.courses.forEach(course => {
              if (course.name) {
                menuCoursesSet.add(course.name);
              }
            });
          }
        });
        const menuCourses = Array.from(menuCoursesSet);
        setCourses(menuCourses);
      } else {
        setCourses([]);
      }
    } else {
      console.log('[PublicViewer-Debug] Processing days for MONTHLY view');
      const weekdaysOnly = menu.days.filter(day => {
        const date = new Date(menu.month);
        date.setDate(day.day);
        const dayOfWeek = date.getDay();
        return dayOfWeek >= 1 && dayOfWeek <= 5;
      });

      const sortedValidDays = weekdaysOnly.sort((a, b) => a.day - b.day);
      setValidDays(sortedValidDays);
      console.log('[PublicViewer-Debug] Monthly view: Valid days for mobile:', sortedValidDays);
      
      // New logic for setting selectedDay in monthly view
      if (sortedValidDays.length > 0) {
        const currentSelectedDayIsValid = sortedValidDays.some(d => d.day === selectedDay);
        
        if (!currentSelectedDayIsValid) {
          // Current selectedDay is not valid for the new menu data, find a new default
          const currentActualDayNumber = new Date().getDate();
          // Check if the current month/year is being viewed
          const viewingCurrentMonthAndYear = 
            menu.month === (new Date().getMonth() + 1).toString().padStart(2, '0') && // menu.month is string 'YYYY-MM-DD'
            new Date(menu.month).getFullYear() === new Date().getFullYear();

          let newSelectedDayValue;
          if (viewingCurrentMonthAndYear && sortedValidDays.some(d => d.day === currentActualDayNumber)) {
            newSelectedDayValue = currentActualDayNumber;
          } else {
            // Try to find the first valid day >= current actual day (if in current month)
            // or just the first valid day of the month otherwise.
            const targetDayForSearch = viewingCurrentMonthAndYear ? currentActualDayNumber : 1;
            const nextHighestIndex = sortedValidDays.findIndex(d => d.day >= targetDayForSearch);
            if (nextHighestIndex !== -1) {
              newSelectedDayValue = sortedValidDays[nextHighestIndex].day;
            } else {
              // Fallback to the last valid day if no day is >= targetDayForSearch (e.g., navigating to a past month)
              // Or if all valid days are before the currentActualDayNumber in the current month.
              newSelectedDayValue = sortedValidDays[sortedValidDays.length - 1].day;
            }
          }
          console.log(`[PublicViewer-Debug] Monthly view: Current selectedDay ${selectedDay} is invalid or not set. Resetting to ${newSelectedDayValue}.`);
          setSelectedDay(newSelectedDayValue);
        } else {
          // selectedDay is already valid for the current sortedValidDays, no change needed.
          console.log(`[PublicViewer-Debug] Monthly view: Current selectedDay ${selectedDay} is valid. No change.`);
        }
      } else {
        // No valid days in the menu
        if (selectedDay !== null) {
          setSelectedDay(null);
          console.log('[PublicViewer-Debug] Monthly view: No valid days, setting selectedDay to null.');
        }
      }

      const weeks = [];
      let currentWeek = [];
      weekdaysOnly.forEach(day => {
        const date = new Date(menu.month);
        date.setDate(day.day);
        const dayOfWeek = date.getDay();
        if (dayOfWeek === 1 || currentWeek.length === 0) {
          if (currentWeek.length > 0) {
            weeks.push(currentWeek);
          }
          currentWeek = Array(5).fill(null);
        }
        currentWeek[dayOfWeek - 1] = day;
      });
      if (currentWeek.length > 0) {
        weeks.push(currentWeek);
      }
      setDays(weeks);
      
      const weekdayNames = Array.from({ length: 5 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - date.getDay() + i + 1);
        const dayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
        return dayName.charAt(0).toUpperCase() + dayName.slice(1);
      });
      setWeekDays(weekdayNames);
      
      if (weekdaysOnly.length > 0) {
        const menuCoursesSet = new Set();
        weekdaysOnly.forEach(day => {
          if (day.items && day.items.length > 0) {
            day.items.forEach(item => {
              if (item.course_name) {
                menuCoursesSet.add(item.course_name);
              }
            });
          }
        });
        const menuCourses = Array.from(menuCoursesSet);
        setCourses(menuCourses);
      } else {
        setCourses([]);
      }
    }
  }, [menu, i18n.language, isWeeklyView]); // REMOVED selectedDay from dependencies

  // Fetch menu data and menu types
  useEffect(() => {
    const fetchData = async () => {
      if (schoolLoading) return;
      
      try {
        setLoading(true);
        setError(null);
        setMenuError(null);
        
        if (!school) {
          throw new Error('No school found');
        }
        
        // Set current month/year
        const now = new Date();
        const month = now.getMonth() + 1;  // Date.getMonth() is 0-11, we want 1-12
        const year = now.getFullYear();
        
        setCurrentMonth(month);
        setCurrentYear(year);
        
        // Use the menu types from our React Query hook
        setCheckingMenuTypes(isLoadingMenuTypes);
        
        // If there are no available menu types, show an error
        if (availableTypes.length === 0 && !isLoadingMenuTypes) {
          setError(t('menu.noMenusAvailable'));
          setLoading(false);
          return;
        }
        
        // Only continue if we have menu types
        if (!isLoadingMenuTypes && availableTypes.length > 0) {
          // Also set this for backward compatibility
          setMenuTypes(availableTypes);
        
          // Check if there's a stored menu type preference for this school
          let selectedMenuTypeFromStorage = null;
          if (school.subdomain) {
            const storedMenuTypeId = localStorage.getItem(`selectedMenuType_${school.subdomain}`);
            if (storedMenuTypeId) {
              // Convert string to number
              const menuTypeId = parseInt(storedMenuTypeId, 10);
              // Check if this menu type is in the available list
              if (availableTypes.some(mt => mt.id === menuTypeId)) {
                selectedMenuTypeFromStorage = menuTypeId;
              }
            }
          }
          
          // Use the stored menu type if available, otherwise fall back to default logic
          let menuTypeIdToUse;
          
          if (selectedMenuTypeFromStorage) {
            // User has a previously selected menu type that's available for the current month
            menuTypeIdToUse = selectedMenuTypeFromStorage;
          } else {
            // Use school's default menu type only if it's in the available list
            const defaultMenuTypeId = school.default_menu_type_id;
            const isDefaultMenuTypeAvailable = defaultMenuTypeId && 
                                            availableTypes.some(mt => mt.id === defaultMenuTypeId);
            
            // If default menu type is available, use it; otherwise use first available menu type
            menuTypeIdToUse = isDefaultMenuTypeAvailable ? defaultMenuTypeId : 
                               (availableTypes.length > 0 ? availableTypes[0].id : null);
          }
          
          setSelectedMenuTypeId(menuTypeIdToUse);
          
          if (menuTypeIdToUse) {
            // Fetch all courses for this menu type (even if they don't have menu items)
            try {
              console.log(`[PublicViewer] About to fetch courses for menu type ${menuTypeIdToUse}`);
              // Use public API endpoint instead of the authenticated admin endpoint
              const coursesData = await menuService.getPublicMenuTypeCourses(
                menuTypeIdToUse,
                i18n.language
              );
              console.log(`[PublicViewer] Successfully fetched ${coursesData.length} courses for menu type ${menuTypeIdToUse}`);
              setMenuTypeCourses(coursesData);
            } catch (err) {
              console.error(`[PublicViewer] Failed to fetch menu type courses for public view:`, err);
              // Don't throw error, just set empty courses array
              setMenuTypeCourses([]);
            }
          } else {
            console.log(`[PublicViewer] No menu type ID available to fetch courses`);
          }
        }
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError('Failed to load menu. Please try again later.');
      } finally {
        setLoading(false);
        setCheckingMenuTypes(false);
      }
    };
    
    fetchData();
  }, [schoolLoading, school, i18n.language, t, availableTypes, isLoadingMenuTypes]);

  // Use React Query hook for menu data - choose between monthly or weekly based on school setting
  const { 
    data: monthlyMenuData,
    isLoading: isLoadingMonthlyMenu,
    error: monthlyMenuQueryError,
    refetch: refetchMonthlyMenu
  } = usePublicMenu(
    isWeeklyView ? null : school?.subdomain, // Only enable for monthly view 
    isWeeklyView ? null : selectedMenuTypeId, 
    isWeeklyView ? null : viewMonth, // Use view month instead of current month
    isWeeklyView ? null : viewYear    // Use view year instead of current year
  );

  // Use React Query hook for weekly menu data
  const {
    data: weeklyMenuData,
    isLoading: isLoadingWeeklyMenu,
    error: weeklyMenuQueryError,
    refetch: refetchWeeklyMenu
  } = usePublicWeekMenu(
    isWeeklyView ? school?.subdomain : null, // Only enable for weekly view
    isWeeklyView ? selectedMenuTypeId : null,
    isWeeklyView ? viewWeek : null
  );

  // Determine which menu data to use based on view mode
  const menuData = isWeeklyView ? weeklyMenuData : monthlyMenuData;
  const isLoadingMenu = isWeeklyView ? isLoadingWeeklyMenu : isLoadingMonthlyMenu;
  const menuQueryError = isWeeklyView ? weeklyMenuQueryError : monthlyMenuQueryError;
  const refetchMenu = isWeeklyView ? refetchWeeklyMenu : refetchMonthlyMenu;
  console.log(`[PublicViewer-Debug] isWeeklyView: ${isWeeklyView}`);
  console.log(`[PublicViewer-Debug] isLoadingMenu: ${isLoadingMenu}`);
  console.log(`[PublicViewer-Debug] Using menuData:`, menuData);

  // Update menu state when menu data changes
  useEffect(() => {
    console.log('[PublicViewer-Debug] useEffect for menuData update triggered');
    if (menuData) {
      console.log(`[PublicViewer-Debug] Received menu data for processing:`, menuData);
      // Log the tags property specifically
      console.log(`[PublicViewer-Debug] menuData.tags:`, menuData.tags);
      // Even if the API returns a 404, it will include an empty days array and possibly customContent
      // So we should still set the menu state with what we have
      setMenu(menuData);
      
      // If the API returned an error message but still gave us data, capture that error
      if (menuData.error) {
        console.log(`[PublicViewer-Debug] Menu data includes error: ${menuData.error}`);
        setMenuError(menuData.error);
      } else {
        console.log('[PublicViewer-Debug] Resetting menuError to null');
        setMenuError(null);
      }
    } else if (menuQueryError) {
      console.error('[PublicViewer-Debug] Failed to fetch menu data:', menuQueryError);
      setMenuError(t('menu.noMenuForType'));
      setMenu(null);
    } else {
      console.log('[PublicViewer-Debug] menuData is null or undefined, not updating menu state');
    }
  }, [menuData, menuQueryError, t]);

  // Handle menu type change - adjust carousel if needed
  const handleMenuTypeChange = (menuTypeId) => {
    setSelectedMenuTypeId(menuTypeId);

    // Check if the selected type is a favorite and carousel is active
    const isFavorite = favoriteMenuTypeIds.includes(menuTypeId);
    const isCarouselActive = actualFavoriteMenuTypes.length >= 4;

    if (isFavorite && isCarouselActive) {
      // Find the index of the selected favorite in the actual favorites list
      const favIndex = actualFavoriteMenuTypes.findIndex(mt => mt.id === menuTypeId);
      
      // Adjust carousel index if the selected favorite is not currently visible
      if (favIndex !== -1) { // Should always be found if isFavorite is true, but check anyway
        // We want favIndex to be >= carouselIndex and <= carouselIndex + 1
        if (favIndex < carouselIndex) {
           // Selected favorite is to the left of the current view
           setCarouselIndex(favIndex);
        } else if (favIndex > carouselIndex + 1) {
          // Selected favorite is to the right of the current view
          // Set carouselIndex so the selected favorite is the second item shown
          setCarouselIndex(favIndex - 1);
        }
        // If favIndex is already carouselIndex or carouselIndex + 1, do nothing
      }
    }
    
    // When menu type changes, we need to reload menu data
    // This is handled by the effect that watches selectedMenuTypeId
    
    // For better user experience, also reset any periods back to current
    if (isWeeklyView) {
      const today = new Date();
      const getWeekNumber = (d) => {
        d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
        return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
      };
      
      setViewWeek(getWeekNumber(today));
    } else {
      const today = new Date();
      setViewMonth(today.getMonth() + 1);
      setViewYear(today.getFullYear());
    }
  };

  // Handle toggling favorite status for the currently selected menu type
  const handleToggleFavorite = () => {
    if (!selectedMenuTypeId) return; // Cannot favorite if nothing is selected
    console.log(`[DEBUG-ToggleFavorite] Toggling favorite for ID: ${selectedMenuTypeId}`);

    setFavoriteMenuTypeIds(prevFavorites => {
      console.log(`[DEBUG-ToggleFavorite] Previous favorites state:`, prevFavorites);
      let nextFavorites;
      if (prevFavorites.includes(selectedMenuTypeId)) {
        // Remove from favorites
        nextFavorites = prevFavorites.filter(id => id !== selectedMenuTypeId);
        console.log(`[DEBUG-ToggleFavorite] Removing ID ${selectedMenuTypeId}`);
      } else {
        // Add to favorites
        nextFavorites = [...prevFavorites, selectedMenuTypeId];
        console.log(`[DEBUG-ToggleFavorite] Adding ID ${selectedMenuTypeId}`);
      }
      console.log(`[DEBUG-ToggleFavorite] Next favorites state:`, nextFavorites);
      return nextFavorites;
    });
  };

  // Save language preference in a cookie
  useEffect(() => {
    if (i18n.language) {
      document.cookie = `preferredLanguage=${i18n.language}; path=/; max-age=31536000`; // 1 year
    }
  }, [i18n.language]);

  // Day navigation handlers for mobile view
  const handlePreviousDay = () => {
    if (isWeeklyView) {
      if (selectedDay > 0) {
        setSelectedDay(selectedDay - 1);
      }
    } else {
    const currentIndex = validDays.findIndex(day => day.day === selectedDay);
    if (currentIndex > 0) {
      setSelectedDay(validDays[currentIndex - 1].day);
      }
    }
  };

  const handleNextDay = () => {
    if (isWeeklyView) {
      if (selectedDay < validDays.length - 1) {
        setSelectedDay(selectedDay + 1);
      }
    } else {
    const currentIndex = validDays.findIndex(day => day.day === selectedDay);
    if (currentIndex !== -1 && currentIndex < validDays.length - 1) {
      setSelectedDay(validDays[currentIndex + 1].day);
      }
    }
  };

  // Get the full data object for the currently selected day (for mobile view)
  const getSelectedDayData = useCallback(() => {
    if (isWeeklyView) {
      // For weekly view, selectedDay is the index in the validDays array
      return selectedDay !== null && validDays.length > 0 ? validDays[selectedDay] : null;
    } else {
      // For monthly view, selectedDay is the day number
    return validDays.find(day => day.day === selectedDay);
    }
  }, [validDays, selectedDay, isWeeklyView]);

  // Format day name and number for display
  const formatDayHeader = (dayData) => {
    if (!dayData) return '';
    
    if (isWeeklyView) {
      // For weekly view, we have the full date in dayData.date
      const date = new Date(dayData.date);
      const dayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
      const dayNumber = date.getDate();
      const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${capitalizedDayName} ${dayNumber}`;
    } else {
      // For monthly view, we use the month from menu and day.day
    const date = new Date(menu.month);
    date.setDate(dayData.day);
    const dayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
    const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `${capitalizedDayName} ${dayData.day}`;
    }
  }
  
  // Update month selection based on the current date and navigation settings
  useEffect(() => {
    if (!currentMonth || !currentYear) return;
    
    // Only set view month/year when they haven't been set yet (initial load)
    if (viewMonth === null || viewYear === null) {
      setViewMonth(currentMonth);
      setViewYear(currentYear);
    }
  }, [currentMonth, currentYear, viewMonth, viewYear]);

  // Helper function to get ISO week number (fewest dependencies)
  const getISOWeekNumber = useCallback((date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }, []);

  // Helper function to get the last week number of a year (depends on getISOWeekNumber)
  const getLastWeekOfYear = useCallback((year) => {
    const d = new Date(year, 11, 28); // Dec 28th is always in the last week
    return getISOWeekNumber(d);
  }, [getISOWeekNumber]);

  // Helper function to get the current week string in YYYY-WW format (depends on getISOWeekNumber)
  const getDefaultWeekString = useCallback(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(getISOWeekNumber(today)).padStart(2, '0')}`;
  }, [getISOWeekNumber]);
    
  // Calculate available periods for navigation (ALWAYS relative to today)
  const calculateAvailablePeriods = useCallback(() => {
    console.log('[PublicViewer-Debug][calculateAvailablePeriods] Starting calculation (relative to today)...');
    const result = [];

    if (!school) {
      console.log('[PublicViewer-Debug][calculateAvailablePeriods] No school data, returning empty result.');
      return result;
    }

    const {
      enable_public_month_navigation,
      months_back_limit = 0,
      months_ahead_limit = 0,
      days_after_month_start_for_back_navigation = 0,
      days_before_month_end_for_forward_navigation = 0
    } = school;

    console.log('[PublicViewer-Debug][calculateAvailablePeriods] School Settings:', {
      enable_public_month_navigation,
      months_back_limit,
      months_ahead_limit,
      days_after_month_start_for_back_navigation,
      days_before_month_end_for_forward_navigation,
      isWeeklyView
    });
    
    const today = new Date();

    if (!enable_public_month_navigation) {
      if (isWeeklyView) {
        const currentActualWeekStr = getDefaultWeekString();
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Navigation disabled, returning current week:', currentActualWeekStr);
        return [{ week: currentActualWeekStr }];
      } else {
        const currentActualMonth = today.getMonth() + 1;
        const currentActualYear = today.getFullYear();
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Navigation disabled, returning current month:', { month: currentActualMonth, year: currentActualYear });
        return [{ month: currentActualMonth, year: currentActualYear }];
      }
    }

    let canGoBackPermission = false;
    let canGoForwardPermission = false;

    if (isWeeklyView) {
      const currentDayOfWeek = today.getDay(); // 0 (Sun) to 6 (Sat)
      console.log('[PublicViewer-Debug][calculateAvailablePeriods] Weekly View Permission Check. Current day of week:', currentDayOfWeek);

      const weeklyBackLimit = months_back_limit;
      const weeklyAheadLimit = months_ahead_limit;

      // --- Backward Navigation Permission (Weekly) ---
      if (weeklyBackLimit > 0) {
        if (days_after_month_start_for_back_navigation > 0) { // Day rule is active
          // Allow back if current day is within the configured number of days from week start (Sun=0)
          if (currentDayOfWeek <= days_after_month_start_for_back_navigation - 1) { 
            canGoBackPermission = true;
            console.log(`[PublicViewer-Debug][calculateAvailablePeriods] Can go back (day rule active [<= ${days_after_month_start_for_back_navigation - 1}], day ${currentDayOfWeek} OK, week limit OK).`);
          } else {
            canGoBackPermission = false; // Day rule active, but day not OK
            console.log(`[PublicViewer-Debug][calculateAvailablePeriods] Cannot go back (day rule active [<= ${days_after_month_start_for_back_navigation - 1}], day ${currentDayOfWeek} NOT OK).`);
          }
        } else { // No day rule, only week limit applies
          canGoBackPermission = true;
          console.log('[PublicViewer-Debug][calculateAvailablePeriods] Can go back (no day rule, week limit OK).');
        }
      } else { // No weeks back allowed by limit
        canGoBackPermission = false;
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Cannot go back (week limit is 0).');
      }

      // --- Forward Navigation Permission (Weekly) ---
      if (weeklyAheadLimit > 0) {
        if (days_before_month_end_for_forward_navigation > 0) { // Day rule is active
          // Allow forward if remaining days in week (from Sat=6) is less than the setting
          const remainingDays = 6 - currentDayOfWeek;
          if (remainingDays < days_before_month_end_for_forward_navigation) { 
            canGoForwardPermission = true;
            console.log(`[PublicViewer-Debug][calculateAvailablePeriods] Can go forward (day rule active [< ${days_before_month_end_for_forward_navigation} days left], ${remainingDays} days left OK, week limit OK).`);
          } else {
            canGoForwardPermission = false; // Day rule active, but day not OK
            console.log(`[PublicViewer-Debug][calculateAvailablePeriods] Cannot go forward (day rule active [< ${days_before_month_end_for_forward_navigation} days left], ${remainingDays} days left NOT OK).`);
          }
        } else { // No day rule, only week limit applies
          canGoForwardPermission = true;
          console.log('[PublicViewer-Debug][calculateAvailablePeriods] Can go forward (no day rule, week limit OK).');
        }
      } else { // No weeks ahead allowed by limit
        canGoForwardPermission = false;
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Cannot go forward (week limit is 0).');
      }
    } else { // Monthly view permissions (remains as previously established)
      const currentDayOfMonth = today.getDate();
      const currentActualMonth = today.getMonth() + 1;
      const currentActualYear = today.getFullYear();
      const daysInCurrentMonth = new Date(currentActualYear, currentActualMonth, 0).getDate();
      console.log('[PublicViewer-Debug][calculateAvailablePeriods] Monthly View Permission Check. Current day of month:', currentDayOfMonth);

      // Monthly Back Permission
      if (months_back_limit > 0) {
        if (days_after_month_start_for_back_navigation > 0) {
          if (currentDayOfMonth <= days_after_month_start_for_back_navigation) {
            canGoBackPermission = true;
          } else {
            canGoBackPermission = false;
          }
        } else {
          canGoBackPermission = true;
        }
      } else {
        canGoBackPermission = false;
      }

      // Monthly Forward Permission
      if (months_ahead_limit > 0) {
        if (days_before_month_end_for_forward_navigation > 0) {
          if ((daysInCurrentMonth - currentDayOfMonth + 1) <= days_before_month_end_for_forward_navigation) {
            canGoForwardPermission = true;
          } else {
            canGoForwardPermission = false;
          }
        } else {
          canGoForwardPermission = true;
        }
      } else {
        canGoForwardPermission = false;
      }
      console.log('[PublicViewer-Debug][calculateAvailablePeriods] Monthly Permissions: ', { canGoBackPermission, canGoForwardPermission });
    }
    console.log('[PublicViewer-Debug][calculateAvailablePeriods] Permissions based on today:', { canGoBackPermission, canGoForwardPermission });

    // --- Generate Periods (relative to today) ---
    // Note: For weekly view, months_back_limit and months_ahead_limit are interpreted as week limits directly.
    const weeksBackLimitToUse = isWeeklyView ? months_back_limit : months_back_limit * 4;
    const weeksAheadLimitToUse = isWeeklyView ? months_ahead_limit : months_ahead_limit * 4;
    // For monthly view, use the direct month limits for clarity in the loop, though result is same.
    const monthlyBackLimitToUse = months_back_limit;
    const monthlyAheadLimitToUse = months_ahead_limit;

    if (isWeeklyView) {
      const currentActualWeekStr = getDefaultWeekString();
      let [currentActualYear, currentActualWeekNum] = currentActualWeekStr.split('-').map(Number);
      if (isNaN(currentActualYear) || isNaN(currentActualWeekNum)) {
          console.error("[PublicViewer-Debug][calculateAvailablePeriods] Failed to parse current actual week string:", currentActualWeekStr);
          return [{ week: new Date().getFullYear() + '-01' }];
      }
      console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating weekly periods relative to:', currentActualWeekStr);

      if (canGoBackPermission && weeksBackLimitToUse > 0) {
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating previous weeks relative to today. Limit:', weeksBackLimitToUse);
        for (let i = weeksBackLimitToUse; i >= 1; i--) {
          let weekNum = currentActualWeekNum - i;
          let weekYear = currentActualYear;
          while (weekNum <= 0) { 
            weekYear -= 1;
            // Ensure getLastWeekOfYear is available and correct
            const lastWeekPrevYear = getLastWeekOfYear(weekYear);
            if (isNaN(lastWeekPrevYear) || lastWeekPrevYear <= 0) {
              console.error("[PublicViewer-Debug][calculateAvailablePeriods] Invalid last week number for year:", weekYear);
              break; // Avoid infinite loop
            }
            weekNum += lastWeekPrevYear;
          }
          result.push({ week: `${weekYear}-${String(weekNum).padStart(2, '0')}` });
        }
      }
      // Add current actual week
      result.push({ week: currentActualWeekStr });
      // Add future weeks
      if (canGoForwardPermission && weeksAheadLimitToUse > 0) {
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating next weeks relative to today. Limit:', weeksAheadLimitToUse);
        for (let i = 1; i <= weeksAheadLimitToUse; i++) {
          let weekNum = currentActualWeekNum + i;
          let weekYear = currentActualYear;
          let lastWeekCurrentYear = getLastWeekOfYear(weekYear);
          if (isNaN(lastWeekCurrentYear) || lastWeekCurrentYear <= 0) {
              console.error("[PublicViewer-Debug][calculateAvailablePeriods] Invalid last week number for year:", weekYear);
              break; // Avoid infinite loop
          }
          while (weekNum > lastWeekCurrentYear) {
            weekNum -= lastWeekCurrentYear;
            weekYear += 1;
            lastWeekCurrentYear = getLastWeekOfYear(weekYear);
            if (isNaN(lastWeekCurrentYear) || lastWeekCurrentYear <= 0) {
               console.error("[PublicViewer-Debug][calculateAvailablePeriods] Invalid last week number for year:", weekYear);
               weekNum = 1; // Reset to avoid issues, or break
               break;
            }
          }
          result.push({ week: `${weekYear}-${String(weekNum).padStart(2, '0')}` });
        }
      }
    } else { // Monthly view
        const currentActualMonth = today.getMonth() + 1;
        const currentActualYear = today.getFullYear();
        console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating monthly periods relative to:', { month: currentActualMonth, year: currentActualYear });

        if (canGoBackPermission && monthlyBackLimitToUse > 0) {
            console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating previous months relative to today. Limit:', monthlyBackLimitToUse);
            for (let i = monthlyBackLimitToUse; i >= 1; i--) {
                let monthBack = currentActualMonth - i;
                let yearBack = currentActualYear;
                while (monthBack <= 0) {
                    monthBack += 12;
                    yearBack -= 1;
                }
                result.push({ month: monthBack, year: yearBack });
            }
        }
        result.push({ month: currentActualMonth, year: currentActualYear }); // Current month
        if (canGoForwardPermission && monthlyAheadLimitToUse > 0) {
            console.log('[PublicViewer-Debug][calculateAvailablePeriods] Calculating next months relative to today. Limit:', monthlyAheadLimitToUse);
            for (let i = 1; i <= monthlyAheadLimitToUse; i++) {
                let monthAhead = currentActualMonth + i;
                let yearAhead = currentActualYear;
                while (monthAhead > 12) {
                    monthAhead -= 12;
                    yearAhead += 1;
                }
                result.push({ month: monthAhead, year: yearAhead });
            }
        }
    }

    console.log('[PublicViewer-Debug][calculateAvailablePeriods] Final Result (relative to today): ', result);
    return result;
  }, [
    school, 
    isWeeklyView, 
    // Use helpers related to today's date/week
    getDefaultWeekString, 
    getLastWeekOfYear, 
    getISOWeekNumber 
    // Removed dependency on viewWeek, viewMonth, viewYear
  ]);

  // Update available periods when settings or current date changes
  useEffect(() => {
    if (!school) return; // Wait for school to be loaded
    console.log('[PublicViewer-Debug] Recalculating available periods due to change in dependencies...');
    const periods = calculateAvailablePeriods();
    setAvailablePeriods(periods);
    // The existing log below already shows the result
    console.log(`[PublicViewer] Available ${isWeeklyView ? 'weeks' : 'months'} for navigation:`, periods);
  }, [calculateAvailablePeriods, school, isWeeklyView]); // Dependencies updated

  // Effect to set the initial view period (month/week)
  useEffect(() => {
    if (schoolLoading || !getDefaultWeekString) return; // Wait for school and helpers

    const today = new Date();
    const initialMonth = today.getMonth() + 1;
    const initialYear = today.getFullYear();

    if (viewMonth === null) setViewMonth(initialMonth);
    if (viewYear === null) setViewYear(initialYear);
    
    if (isWeeklyView && viewWeek === null) {
      setViewWeek(getDefaultWeekString());
    }
  }, [schoolLoading, viewMonth, viewYear, isWeeklyView, viewWeek, getDefaultWeekString]);
  
  // Set current month/year based on today's date (runs once)
  useEffect(() => {
    const today = new Date();
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
  }, []); // Empty dependency array: runs once on mount

  // Set initial isWeeklyView and viewing period based on school settings
   useEffect(() => {
    if (!school || schoolLoading || !getDefaultWeekString) return;

    const useWeeklyView = school.monthly_public_display === false;
    setIsWeeklyView(useWeeklyView);

    const today = new Date();
    const initialMonth = today.getMonth() + 1;
    const initialYear = today.getFullYear();

    if (!viewMonth) setViewMonth(initialMonth);
    if (!viewYear) setViewYear(initialYear);

    if (useWeeklyView && !viewWeek) {
      setViewWeek(getDefaultWeekString());
    }
    console.log(`[PublicViewer] Initial display mode: ${useWeeklyView ? 'weekly' : 'monthly'}. Initial week: ${useWeeklyView ? getDefaultWeekString() : 'N/A'}`);

  }, [school, schoolLoading, getDefaultWeekString, viewMonth, viewYear, viewWeek]); // Added viewMonth, viewYear, viewWeek

  // Force a refetch of menu data when selected parameters change
  useEffect(() => {
    if (selectedMenuTypeId && school?.subdomain) {
      if (isWeeklyView) {
        // Weekly view - fetch based on viewWeek
        if (viewWeek) {
          console.log(`[PublicViewer] Fetching weekly menu for type ${selectedMenuTypeId}, week ${viewWeek}`);
          refetchMenu();
          
          // Store the selected menu type in localStorage for this school
          localStorage.setItem(`selectedMenuType_${school.subdomain}`, selectedMenuTypeId.toString());
          
          // Fetch all courses for the selected menu type
          const fetchCourses = async () => {
            try {
              const coursesData = await menuService.getPublicMenuTypeCourses(
                selectedMenuTypeId,
                i18n.language
              );
              setMenuTypeCourses(coursesData);
            } catch (err) {
              console.error('Failed to fetch menu type courses for public view:', err);
              setMenuTypeCourses([]);
            }
          };
          
          fetchCourses();
          setLoadingPeriodData(false);
        }
      } else {
        // Monthly view - fetch based on viewMonth/viewYear
        if (viewMonth && viewYear) {
          console.log(`[PublicViewer] Fetching monthly menu for type ${selectedMenuTypeId}, month ${viewMonth}/${viewYear}`);
          refetchMenu();
          
          // Store the selected menu type in localStorage for this school
          localStorage.setItem(`selectedMenuType_${school.subdomain}`, selectedMenuTypeId.toString());
          
          // Fetch all courses for the selected menu type
          const fetchCourses = async () => {
            try {
              const coursesData = await menuService.getPublicMenuTypeCourses(
                selectedMenuTypeId,
                i18n.language
              );
              setMenuTypeCourses(coursesData);
            } catch (err) {
              console.error('Failed to fetch menu type courses for public view:', err);
              setMenuTypeCourses([]);
            }
          };
          
          fetchCourses();
          setLoadingPeriodData(false);
        }
      }
    }
  }, [selectedMenuTypeId, school?.subdomain, refetchMenu, i18n.language, viewMonth, viewYear, viewWeek, isWeeklyView]);

  // Handler for period (month/week) navigation
  const handlePeriodChange = (period) => {
    // Check if navigation is enabled directly from the school object
    if (!school?.enable_public_month_navigation) return;
    
    if (isWeeklyView) {
      // Weekly view navigation
      if (period.week) {
        setViewWeek(period.week);
        setLoadingPeriodData(true);
        
        // Update the selected day
        // When changing weeks, select the first day of the week (Sunday)
        const [weekYear, weekNum] = period.week.split('-').map(Number);
        const firstDayOfWeek = getFirstDayOfWeek(weekYear, weekNum);
        setSelectedDay(firstDayOfWeek.getDate());
      }
    } else {
      // Monthly view navigation
      setViewMonth(period.month);
      setViewYear(period.year);
      setLoadingPeriodData(true);
    
    // Reset selected day when changing months
    const today = new Date();
      if (period.month === today.getMonth() + 1 && period.year === today.getFullYear()) {
      setSelectedDay(today.getDate());
    } else {
        if ((period.month > today.getMonth() + 1 && period.year === today.getFullYear()) || 
            (period.month === today.getMonth() + 1 && period.year > today.getFullYear())) {
        setSelectedDay(1); // Default to first day of month when viewing future months
      } else {
          // Calculate the last day of the previous month
          const lastDay = new Date(period.year, period.month, 0).getDate();
        setSelectedDay(lastDay); // Default to last day of the previous month when viewing previous months
        }
      }
    }
  };

  // Get the first day of a specific week in a year
  const getFirstDayOfWeek = (year, weekNumber) => {
    // January 4th is always in the first week
    const januaryFourth = new Date(year, 0, 4);
    // Get the day of week (0-6, where 0 is Sunday)
    const dayOfWeek = januaryFourth.getDay();
    // Calculate days to first day of first week (previous Sunday)
    const daysToFirstDay = dayOfWeek;
    // Get first day of first week
    const firstDayOfFirstWeek = new Date(year, 0, 4 - daysToFirstDay);
    // Calculate first day of requested week
    const firstDayOfRequestedWeek = new Date(firstDayOfFirstWeek);
    firstDayOfRequestedWeek.setDate(firstDayOfFirstWeek.getDate() + (weekNumber - 1) * 7);
    
    return firstDayOfRequestedWeek;
  };

  // Handlers for chevron period navigation
  const handlePreviousPeriod = () => {
    console.log('[PublicViewer-Debug][handlePreviousPeriod] Clicked. Current viewWeek:', viewWeek, 'Available periods:', availablePeriods);
    const currentIndex = isWeeklyView
      ? availablePeriods.findIndex(p => p.week === viewWeek)
      : availablePeriods.findIndex(p => p.month === viewMonth && p.year === viewYear);
    console.log('[PublicViewer-Debug][handlePreviousPeriod] Current index:', currentIndex);
      
    if (currentIndex > 0) {
      console.log('[PublicViewer-Debug][handlePreviousPeriod] Navigating to period:', availablePeriods[currentIndex - 1]);
      handlePeriodChange(availablePeriods[currentIndex - 1]);
    } else {
      console.log('[PublicViewer-Debug][handlePreviousPeriod] Cannot go further back or current index not found.');
    }
  };

  const handleNextPeriod = () => {
    console.log('[PublicViewer-Debug][handleNextPeriod] Clicked. Current viewWeek:', viewWeek, 'Available periods:', availablePeriods);
    const currentIndex = isWeeklyView
      ? availablePeriods.findIndex(p => p.week === viewWeek)
      : availablePeriods.findIndex(p => p.month === viewMonth && p.year === viewYear);
    console.log('[PublicViewer-Debug][handleNextPeriod] Current index:', currentIndex);
      
    if (currentIndex !== -1 && currentIndex < availablePeriods.length - 1) {
      console.log('[PublicViewer-Debug][handleNextPeriod] Navigating to period:', availablePeriods[currentIndex + 1]);
      handlePeriodChange(availablePeriods[currentIndex + 1]);
    } else {
      console.log('[PublicViewer-Debug][handleNextPeriod] Cannot go further forward or current index not found.');
    }
  };

  // Function to get proper image URL for tag logo
  const getTagLogoUrl = (logoPath) => {
    if (!logoPath) return '';
    
    // If the path already has a protocol, return it as is
    if (logoPath.startsWith('http')) return logoPath;
    
    // Handle UUID format (Nhost storage)
    if (logoPath.includes('-') && !logoPath.includes('/')) {
      return `https://${process.env.REACT_APP_NHOST_SUBDOMAIN || 'gtwosmwmvopoqrodakmd'}.storage.${process.env.REACT_APP_NHOST_REGION || 'eu-central-1'}.nhost.run/v1/files/${logoPath}`;
    }
    
    // For relative paths with leading slash
    if (logoPath.startsWith('/')) {
      return `${window.location.origin}${logoPath}`;
    }
    
    // For legacy path format
    return `${window.location.origin}/api/uploads/logos/${logoPath.split('/').pop()}`;
  };

  // Check school settings for display mode and set up appropriate view
  useEffect(() => {
    if (!schoolLoading && school) {
      // Set weekly view mode based on school setting
      const useWeeklyView = school.monthly_public_display === false;
      setIsWeeklyView(useWeeklyView);
      console.log(`[PublicViewer] Display mode set to ${useWeeklyView ? 'weekly' : 'monthly'} based on school settings`);
      
      // Initialize the current period (month or week)
      const today = new Date();
      const currentMonthValue = today.getMonth() + 1; // 1-12
      const currentYearValue = today.getFullYear();
      
      setCurrentMonth(currentMonthValue);
      setCurrentYear(currentYearValue);
      
      // For weekly view, calculate and set the current week number
      // Ensure getDefaultWeekString and getISOWeekNumber are defined
      if (useWeeklyView && getDefaultWeekString && getISOWeekNumber) {
        const currentWeek = getDefaultWeekString();
        setViewWeek(currentWeek);
        console.log(`[PublicViewer] Current week set to ${currentWeek}`);
      } else {
        // For monthly view, set view month/year
        setViewMonth(currentMonthValue);
        setViewYear(currentYearValue);
      }
    }
  }, [school, schoolLoading, getDefaultWeekString, getISOWeekNumber]); // Added dependencies

  // Format month name with proper capitalization for "de" and "del" in Spanish and Catalan
  const formatMonthName = (monthString) => {
    if (!monthString) return '';
    
    // For all languages, manually capitalize first letter of the string
    let formatted = monthString.charAt(0).toUpperCase() + monthString.slice(1);
    
    // For Spanish and Catalan, ensure "de" and "del" prepositions stay lowercase
    if (i18n.language === 'es' || i18n.language === 'ca') {
      formatted = formatted
        .replace(/\bDe\b/g, 'de')
        .replace(/\bDel\b/g, 'del');
    }
    
    return formatted;
  };

  // Format week display (e.g., "4-10 maig 2025" instead of "Week 35, 2023")
  const formatWeekDisplay = (weekString) => {
    if (!weekString) return '';
    
    // If menu data includes weekStart and weekEnd, use those for a more detailed label
    if (menu && menu.weekStart && menu.weekEnd) {
      const startDate = new Date(menu.weekStart);
      const endDate = new Date(menu.weekEnd);
      
      // Get first day (Monday) and last day (Friday) of the school week
      // weekStart is Sunday, so we need to add 1 day to get Monday
      const firstDay = new Date(startDate.getTime());
      firstDay.setDate(firstDay.getDate() + 1); // Monday
      
      // weekEnd is Saturday, so we need to subtract 1 day to get Friday
      const lastDay = new Date(endDate.getTime());
      lastDay.setDate(lastDay.getDate() - 1); // Friday
      
      // Get day numbers
      const firstDayNum = firstDay.getDate();
      const lastDayNum = lastDay.getDate();
      
      // Format month according to locale (without day/year)
      const month = firstDay.toLocaleDateString(i18n.language, { month: 'long' });
      const year = firstDay.getFullYear();
      
      // Format as "9-13 juin 2025" (Monday-Friday)
      return `${firstDayNum}-${lastDayNum} ${month} ${year}`;
    }
    
    // Fallback to simple week number format if no date range
    const [year, weekNum] = weekString.split('-').map(Number);
    return `${t('menu.weekShort')} ${weekNum}, ${year}`;
  };

  // Format month name with proper capitalization
  const periodLabel = isWeeklyView
    ? formatWeekDisplay(viewWeek)
    : formatMonthName(
        new Date(
          viewYear || currentYear, 
          (viewMonth || currentMonth) - 1
        ).toLocaleString(i18n.language, { month: 'long', year: 'numeric' })
      );

  // Add these helper function definitions to the top of the component, right after imports
  // const getISOWeekNumber = useCallback((date) => { ... });
  // const getLastWeekOfYear = useCallback((year) => { ... }, [getISOWeekNumber]);
  // const getDefaultWeekString = useCallback(() => { ... }, [getISOWeekNumber]);
  // These have been moved up

  if (loading || schoolLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: school?.secondary_bg_color || '#3498DB' }}></div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-black px-4 py-3 rounded">
          School not found
        </div>
      </div>
    );
  }
  
  // Format period name with proper capitalization for "de" and "del" in Spanish and Catalan
  const formatPeriodName = (periodString) => {
    if (!periodString) return '';
    
    // For all languages, manually capitalize first letter of the string
    let formatted = periodString.charAt(0).toUpperCase() + periodString.slice(1);
    
    // For Spanish and Catalan, ensure "de" and "del" prepositions stay lowercase
    if (i18n.language === 'es' || i18n.language === 'ca') {
      formatted = formatted
        .replace(/\bDe\b/g, 'de')
        .replace(/\bDel\b/g, 'del');
    }
    
    return formatted;
  };

  // Format period name with proper capitalization
  const periodDate = viewMonth && viewYear 
    ? new Date(viewYear, viewMonth - 1) 
    : new Date(currentYear, currentMonth - 1); // Fallback to current period
  const periodName = formatPeriodName(periodDate.toLocaleString(i18n.language, { month: 'long', year: 'numeric' }));
  
  // Apply school branding colors from the school object, with fallbacks
  const headerBgColor = school.primary_bg_color || '#4A90E2';
  const headerTextColor = school.primary_text_color || '#FFFFFF';
  const accentColor = school.secondary_bg_color || '#3498DB';
  const accentTextColor = school.secondary_text_color || '#FFFFFF';
  
    
  // If there's a general error (like no menus available for the period),
  // still show the navigation and period/year header with the error below
  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Header with school branding */}
        <header 
          className="p-4 shadow-md flex justify-between items-center"
          style={{ backgroundColor: headerBgColor, color: headerTextColor }}
        >
          <a href={school.homepage_url || '#'} className="flex items-center">
            {school.logo_path && (
              <img 
                src={getPublicUrl(school.logo_path)} 
                alt={school.name || 'School logo'} 
                className="h-12 mr-3" 
              />
            )}
            <h1 className="text-3xl font-bold">{school.name || t('menu.schoolLunchMenu')}</h1>
          </a>
          
          <LanguageSelector />
        </header>
        
        <main className="flex-grow p-4 w-full">
          {/* Month/year header with primary colors */}
          <div 
            className="mb-6 overflow-hidden flex justify-left items-center border-t"
            style={{ 
              borderColor: headerBgColor
            }}
          >
            <div 
              className="px-4 py-3 h-14"
              style={{ 
                backgroundColor: headerBgColor, 
                color: headerTextColor 
              }}
            >
              <h2 className="text-3xl">
                {periodName}
              </h2>
            </div>
            <div 
              className="px-4 py-3 -skew-x-12 -ml-2 h-14"
              style={{ 
                backgroundColor: headerBgColor
              }}
            >
              {/* Empty div with fixed height for skew effect */}
            </div>
            
            {/* Menu type selector placeholder */}
            <div className="flex items-center ml-6">
              <span className="mr-2 text-xl">{t('menu.menuType')}:</span>
              <div className="text-gray-600">
                {t('menu.notAvailable')}
              </div>
            </div>
          </div>
          
          {/* Error message */}
          <div className="flex-grow flex flex-col items-center justify-center p-4 my-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
              <h2 className="text-xl font-semibold mb-4" 
                  style={{ color: headerTextColor, backgroundColor: headerBgColor, padding: '0.5rem', borderRadius: '0.25rem' }}>
                {t('menu.noMenu')}
              </h2>
              <p className="mb-6 text-gray-800">
                {error}
              </p>
            </div>
          </div>
        </main>
        
        <footer 
          className="p-4 text-center text-sm" 
          style={{ backgroundColor: headerBgColor, color: headerTextColor }}
        >
          {t('menu.footer')}
        </footer>
      </div>
    );
  }

  // Handle carousel navigation (with looping)
  const handleCarouselPrev = () => {
    setCarouselIndex(prev => {
      const newIndex = prev - 1;
      // Loop to the end if going past the beginning
      return newIndex < 0 ? actualFavoriteMenuTypes.length - 2 : newIndex;
    });
  };
  const handleCarouselNext = () => {
    setCarouselIndex(prev => {
      const newIndex = prev + 1;
      // Loop to the beginning if going past the end (showing 2 items)
      return newIndex > actualFavoriteMenuTypes.length - 2 ? 0 : newIndex;
    });
  };

  // Get dish name for a specific day and course with fallback translations
  const getDishName = (dayData, courseName) => {
    if (!dayData) return '-';
    
    if (isWeeklyView) {
      // Weekly format: day.courses[].dishes[]
      const course = dayData.courses && dayData.courses.find(c => c.name === courseName);
      if (!course || !course.dishes || course.dishes.length === 0) return '-';
      
      // Return the first dish name for this course
      return course.dishes[0].name || t('menu.dishNotTranslated');
    } else {
      // Monthly format: day.items[]
    const item = dayData.items && dayData.items.find(i => i.course_name === courseName);
    if (!item) return '-';
    
    return item.dish_name || t('menu.dishNotTranslated');
    }
  };
  
  // Regular function for getting dish tags
  const getDishTags = (dayData, courseName) => {
    if (!dayData) return [];
    
    if (isWeeklyView) {
      // Weekly format: day.courses[].dishes[]
      const course = dayData.courses && dayData.courses.find(c => c.name === courseName);
      if (!course || !course.dishes || course.dishes.length === 0) return [];
      
      const dish = course.dishes[0];
      
      // Check if tags directly exist on the dish object
      if (dish.tags) {
        // Return tags if they're already in the expected format
        if (dish.tags.length > 0 && typeof dish.tags[0] === 'object' && dish.tags[0].logo_path) {
          return dish.tags;
        }
        
        // Otherwise, map tag IDs to objects using menu.tags
        if (menu && menu.tags && Array.isArray(dish.tags)) {
          return dish.tags.map(id => {
            const tag = menu.tags.find(a => a.id === id);
            return tag || { id, name: `Tag ${id}`, logo_path: '' };
          });
        }
      }
      
      return [];
    } else {
      // Monthly format: day.items[].dish_tags
      const item = dayData.items && dayData.items.find(i => i.course_name === courseName);
      if (!item || !item.dish_tags) return [];
      
      return item.dish_tags || [];
    }
  };
  
  // Format day cell style based on availability
  const getDayCellStyle = (dayData, courseName) => {
    if (!dayData) return 'border-0'; // Completely empty cell for days not in current period
    
    if (isWeeklyView) {
      // Weekly view
      const course = dayData.courses && dayData.courses.find(c => c.name === courseName);
      const courseHasDishes = course && course.dishes && course.dishes.length > 0;
      
      if (!courseHasDishes) {
        return `bg-gray-100 text-gray-400 border border-gray-200`; // No course item
      }
      
      return 'bg-white text-black border border-gray-200'; // Normal cell with dish - use black text
    } else {
      // Monthly view - original logic
    const courseItemExists = dayData.items && dayData.items.some(item => item.course_name === courseName);
    
    if (!courseItemExists) {
      return `bg-gray-100 text-gray-400 border border-gray-200`; // No course item
    }
    
    return 'bg-white text-black border border-gray-200'; // Normal cell with dish - use black text
    }
  };
  
  // Check if a day has any dishes assigned
  const isDayEmpty = (dayData) => {
    if (!dayData) {
      return true;
    }
    
    if (isWeeklyView) {
      // Weekly view - check if there are any courses with dishes
      return !dayData.courses || dayData.courses.length === 0 || 
             dayData.courses.every(course => !course.dishes || course.dishes.length === 0);
    } else {
      // Monthly view - original logic
      if (!dayData || !dayData.items || dayData.items.length === 0) {
        return true;
      }
    // Check if all items for this day have no dish names
    return dayData.items.every(item => !item.dish_name);
    }
  };
  
  // Add a function to get nutrient summary for weekly view
  const getDayNutrientSummary = (dayData) => {
    if (!dayData || !school?.show_nutrients) {
      return { status: 'unavailable' };
    }

    if (isWeeklyView) {
      // First check if the backend already provided a nutrient_summary
      if (dayData.nutrient_summary) {
        return dayData.nutrient_summary;
      }
      
      // Weekly view - extract nutrients from all dishes
      if (!dayData.courses || dayData.courses.length === 0) {
        return { status: 'unavailable' };
      }

      // Collect all nutrients from all dishes
      let allNutrients = [];
      let hasAllNutrients = true;

      dayData.courses.forEach(course => {
        if (course.dishes && course.dishes.length > 0) {
          course.dishes.forEach(dish => {
            if (dish.nutrients) {
              allNutrients.push(...dish.nutrients);
            } else {
              hasAllNutrients = false;
            }
          });
        }
      });

      if (allNutrients.length === 0) {
        return { status: 'unavailable' };
      }

      // Group nutrients by id and sum their values
      const nutrientMap = {};
      allNutrients.forEach(nutrient => {
        if (!nutrientMap[nutrient.id]) {
          nutrientMap[nutrient.id] = {
            nutrientId: nutrient.id,
            name: nutrient.name,
            unit: nutrient.unit,
            totalValue: 0
          };
        }
        nutrientMap[nutrient.id].totalValue += parseFloat(nutrient.value || 0);
      });

      return {
        status: hasAllNutrients ? 'complete' : 'incomplete',
        nutrients: Object.values(nutrientMap)
      };
    } else {
      // Monthly view - use existing nutrient_summary if available
      return dayData.nutrient_summary || { status: 'unavailable' };
    }
  };
  
  // Add a UI component to show when no menu is available
  // Only show this component if there's no menu AND no specific menu error
  // This allows menu type-specific errors to be handled in the main UI
  if (!loading && !menuError && (!menu || !menu.days || menu.days.length === 0)) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Header with school branding */}
        <header 
          className="p-4 flex justify-between items-center shadow-md" 
          style={{ backgroundColor: headerBgColor, color: headerTextColor }}
        >
          <div className="flex items-center">
            {school.logo_path && (
              <img 
                src={getPublicUrl(school.logo_path)} 
                alt={school.name} 
                className="h-10 mr-2"
              />
            )}
            <h1 className="text-xl font-bold">{t('menu.schoolLunchMenu')}</h1>
          </div>
          <LanguageSelector />
        </header>
        
        <div className="flex-grow flex flex-col items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold" style={{ color: headerTextColor, backgroundColor: headerBgColor, padding: '0.5rem', borderRadius: '0.25rem' }}>
              {t('menu.noMenu')}
            </h2>
          </div>
        </div>
      </div>
    );
  }

  // Combine both menu type courses and courses found in the menu
  const allCourses = [...menuTypeCourses];
  
  if (courses && courses.length > 0) {
    courses.forEach(courseName => {
      // Check if this course is not already in the menuTypeCourses list
      const existingCourse = allCourses.find(c => c.name === courseName);
      if (!existingCourse) {
        // Add as a simple object to maintain consistency
        // Assign a high display_order for courses not in menuTypeCourses
        allCourses.push({ name: courseName, display_order: 999 });
      }
    });
  }

  // Sort allCourses by display_order to ensure proper ordering
  allCourses.sort((a, b) => {
    const orderA = a.display_order || 999;
    const orderB = b.display_order || 999;
    return orderA - orderB;
  });

  // Debugging: Log the types being used for buttons
  console.log('[DEBUG-Favorites] Available Types:', availableTypes);
  console.log('[DEBUG-Favorites] Actual Favorite Menu Types (used for buttons):', actualFavoriteMenuTypes);

  console.log('[PublicViewer-Debug] Render: State before return - isWeeklyView:', isWeeklyView);
  console.log('[PublicViewer-Debug] Render: State before return - days:', days);
  console.log('[PublicViewer-Debug] Render: State before return - allCourses:', allCourses);
  console.log('[PublicViewer-Debug] Render: State before return - menuError:', menuError);
  console.log('[PublicViewer-Debug] Render: State before return - error:', error);
  console.log('[PublicViewer-Debug] Render: State before return - isLoadingMenu:', isLoadingMenu);
  console.log('[PublicViewer-Debug] Render: State before return - schoolLoading:', schoolLoading);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with school branding */}
      <header 
        className="p-4 shadow-md flex justify-between items-center"
        style={{ backgroundColor: headerBgColor, color: headerTextColor }}
      >
        <a href={school.homepage_url || '#'} className="flex items-center">
          {school.logo_path && (
            <img 
              src={getPublicUrl(school.logo_path)} 
              alt={school.name || 'School logo'} 
              className="h-12 mr-3" 
            />
          )}
          <h1 className="text-3xl font-bold">{school.name || t('menu.schoolLunchMenu')}</h1>
        </a>
        
        <LanguageSelector />
      </header>
      
      <main className="flex-grow p-4 w-full">
        {/* ---- Top Bar: Period, Day Selector (Mobile), Controls ---- */}
        <div 
          className="mb-6 overflow-hidden flex flex-col md:flex-row md:justify-left md:items-center md:border-t"
            style={{ 
              borderColor: headerBgColor
            }}
        >
          {/* Left part: Period/Year - always visible */}
          <div className="flex items-center justify-between">
            <div 
              className="px-4 py-3 h-14 flex items-center"
              style={{ 
                backgroundColor: headerBgColor, 
                color: headerTextColor 
              }}
            >
              {/* Conditionally render Previous Period (Month/Week) Chevron */}
              {school?.enable_public_month_navigation && 
               availablePeriods.length > 1 &&
               availablePeriods.findIndex(
                 p => isWeeklyView 
                   ? p.week === viewWeek 
                   : (p.month === viewMonth && p.year === viewYear)
               ) > 0 && (
                <button 
                  onClick={handlePreviousPeriod}
                  className="mr-3 p-1 rounded-full hover:bg-white hover:text-black transition-colors"
                  aria-label={isWeeklyView ? t('menu.previousWeek') : t('menu.previousMonth')}
                >
                  <FaChevronLeft />
                </button>
              )}
              
              <h2 className="text-2xl md:text-3xl mx-2 text-center flex-shrink-0">
                {periodLabel}
              </h2>
              
              {/* Conditionally render Next Period (Month/Week) Chevron */}
              {school?.enable_public_month_navigation && 
               availablePeriods.length > 1 &&
               availablePeriods.findIndex(
                 p => isWeeklyView 
                   ? p.week === viewWeek 
                   : (p.month === viewMonth && p.year === viewYear)
               ) < availablePeriods.length - 1 && (
                <button 
                  onClick={handleNextPeriod}
                  className="ml-3 p-1 rounded-full hover:bg-white hover:text-black transition-colors"
                  aria-label={isWeeklyView ? t('menu.nextWeek') : t('menu.nextMonth')}
                >
                  <FaChevronRight />
                </button>
              )}
              
              {/* Loading indicator for period change */}
              {loadingPeriodData && (
                <div className="ml-4 animate-spin">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                </div>
              )}
            </div>
            {/* Skew effect element - can keep or remove for mobile */}
            <div 
              className="px-4 py-3 -skew-x-12 -ml-2 h-14 hidden md:block"
              style={{ 
                backgroundColor: headerBgColor
              }}
            >
              {/* Empty div with fixed height for skew effect */}
            </div>

            {/* Mobile Day Selector (Dropdown) - visible only on small screens */}
            <div className="ml-4 md:hidden">
              <label htmlFor="day-select" className="sr-only">{t('menu.selectDay')}</label>
              <select 
                 id="day-select"
                 value={selectedDay !== null ? selectedDay : ''} 
                 onChange={(e) => {
                   const val = e.target.value;
                   if (val === '') return;
                   setSelectedDay(isWeeklyView ? parseInt(val, 10) : parseInt(val, 10));
                 }} 
                 className="p-2 border border-gray-300 rounded bg-white text-black"
                 disabled={validDays.length === 0}
              >
                {isWeeklyView ? (
                  // For weekly view, show options with index values
                  validDays.map((day, index) => (
                    <option key={day.date} value={index}>
                      {formatDayHeader(day)}
                    </option>
                  ))
                ) : (
                  // For monthly view, show options with day number values
                  validDays.map(day => (
                   <option key={day.day} value={day.day}>
                     {formatDayHeader(day)}
                   </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Right part: Controls (Language, Menu Type, Favorites) - stacked on mobile, inline on desktop */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between md:flex-grow md:ml-auto p-2 md:p-0">
             {/* Menu type selector group */}
             <div className="flex items-center mb-2 md:mb-0 md:ml-6">
                <span className="mr-2 text-lg md:text-xl">{t('menu.menuType')}:</span>
                {isLoadingMenuTypes ? (
                  <div className="px-4 py-2 text-gray-600 italic">
                    {t('menu.checkingAvailableMenus')}
                  </div>
                ) : (
                  <> 
                    <MenuTypeSelector 
                      menuTypes={availableTypes} 
                      selectedMenuTypeId={selectedMenuTypeId} 
                      onChange={handleMenuTypeChange}
                    />
                    {selectedMenuTypeId && ( 
                      <button 
                        onClick={handleToggleFavorite} 
                        className="ml-2 p-2 rounded-full hover:bg-gray-200 transition-colors"
                        title={favoriteMenuTypeIds.includes(selectedMenuTypeId) ? t('menu.removeFromFavorites') : t('menu.addToFavorites')}
                        aria-label={favoriteMenuTypeIds.includes(selectedMenuTypeId) ? t('menu.removeFromFavorites') : t('menu.addToFavorites')}
                      >
                        {favoriteMenuTypeIds.includes(selectedMenuTypeId) ? (
                          <FaHeart className="text-red-500" size="1.2em" />
                        ) : (
                          <FaRegHeart className="text-gray-500" size="1.2em" />
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            
              {/* Favorite Quick Selector - Conditionally rendered */} 
              {actualFavoriteMenuTypes.length > 0 && (
                <div className="flex items-center space-x-2 mb-2 md:mb-0 md:ml-4">
                  <span className="text-sm italic mr-1">{t('menu.favorites')}:</span>
                  {actualFavoriteMenuTypes.length >= 4 && (
                    <button 
                      onClick={handleCarouselPrev} 
                      className="p-1 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                      aria-label={t('menu.previousFavorites')}
                    >
                      <FaChevronLeft />
                    </button>
                  )}
                  {/* Favorite Buttons */} 
                  {(actualFavoriteMenuTypes.length < 4 
                    ? actualFavoriteMenuTypes 
                    : actualFavoriteMenuTypes.slice(carouselIndex, carouselIndex + 2)
                  ).map(favType => (
                    <button 
                      key={favType.id} 
                      onClick={() => handleMenuTypeChange(favType.id)}
                      className={`px-3 py-1 border rounded text-sm transition-colors min-h-12 flex items-center justify-center ${selectedMenuTypeId === favType.id ? 'bg-blue-100 border-blue-300 font-semibold' : 'border-gray-300 hover:bg-gray-100'}`}
                      title={`${t('menu.selectMenuType')} ${favType.translated_name}`}
                    >
                      {favType.translated_name}
                    </button>
                  ))}
                  {actualFavoriteMenuTypes.length >= 4 && (
                    <button 
                      onClick={handleCarouselNext} 
                      className="p-1 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                      aria-label={t('menu.nextFavorites')}
                    >
                      <FaChevronRight />
                    </button>
                  )}
                </div>
              )}
          </div>
        </div>
        
        {/* ---- Menu Display Area ---- */}
        {menuError ? (
          <div className="flex-grow flex flex-col items-center justify-center p-4 my-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
              <h2 className="text-xl font-semibold mb-4" style={{ color: headerTextColor, backgroundColor: headerBgColor, padding: '0.5rem', borderRadius: '0.25rem' }}>
                {t('menu.noMenu')}
              </h2>
              <p className="mb-6 text-gray-800">
                {menuError}
              </p>
            </div>
          </div>
        ) : (
          /* Only display the menu if there's no menu error */
          <>
            {/* ---- Desktop Weekly calendar display ---- */}
            <div className="overflow-x-auto w-full hidden md:block">
              <table className="min-w-full w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  {/* First column for course names - width based on content */}
                  <col style={{ width: 'auto' }} />
                  
                  {/* Equal width columns for each day of the week */}
                  {[0, 1, 2, 3, 4].map(i => (
                    <col key={`col-${i}`} style={{ width: '18%' }} />
                  ))}
                </colgroup>
                <tbody>
                  {days.map((week, weekIndex) => {
                    console.log(`[PublicViewer-Debug] Rendering week ${weekIndex + 1} of ${days.length}`);
                    console.log(`[PublicViewer-Debug] Week data:`, week);
                    // Filter out null days (days not in the current period)
                    const daysInCurrentPeriod = week.filter(day => day !== null);
                    console.log(`[PublicViewer-Debug] Days in this period: ${daysInCurrentPeriod.length}`);
                    
                    // Skip rendering this week if it has no days in the current period
                    if (daysInCurrentPeriod.length === 0) {
                      console.log('[PublicViewer-Debug] Skipping render for empty week.');
                      return null;
                    }
                    
                    return (
                      <React.Fragment key={`week-${weekIndex}`}>
                        {/* Header row with day names and dates */}
                        <tr>
                          <td className="border-0"></td>
                          {weekDays.map((dayName, dayIndex) => {
                            const dayData = week[dayIndex];
                            console.log(`[PublicViewer-Debug] Rendering day header for index ${dayIndex}, DayData:`, dayData);
                            
                            // If dayData is null, render an empty header cell
                            if (!dayData) {
                              return <td key={`day-header-${dayIndex}`} className="border-0"></td>;
                            }
                            
                            // For weekly view, check if the day has any dishes
                            // For monthly view, use the existing check
                            const dayHasContent = !isDayEmpty(dayData);
                            
                            const dayNumber = isWeeklyView ? 
                              (new Date(dayData.date)).getDate() : // Get day number from date for weekly view
                              dayData.day;                         // Use day number directly for monthly view
                            
                            const specialName = dayData.specialName;
                            
                            return (
                              <td 
                                key={`day-header-${dayIndex}`} 
                                className="py-2 px-3 text-center font-2xl border border-gray-200"
                                style={{ 
                                  backgroundColor: dayHasContent ? accentColor : '#555555', 
                                  color: dayHasContent ? accentTextColor : '#FFFFFF',
                                  wordWrap: 'break-word',
                                  hyphens: 'auto'
                                }}
                              >
                                <div style={{ wordWrap: 'break-word', hyphens: 'auto', maxWidth: '100%' }}>{dayName} {dayNumber && <span>{dayNumber}</span>}</div>
                                {specialName && (
                                  <div className="text-sm font-normal italic" style={{ wordBreak: 'break-word', wordWrap: 'break-word', hyphens: 'auto', maxWidth: '100%' }}>
                                    {specialName}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        
                        {/* Course rows */}
                        {allCourses.map((course, courseIndex) => {
                          console.log(`[PublicViewer-Debug] Rendering course row for:`, course);
                          return (
                          <tr key={`course-${courseIndex}-week-${weekIndex}`} className="text-center">
                            <td className="border-0 text-right italic text-gray-600 pr-2">
                              {course.name}
                            </td>
                            {week.map((dayData, dayIndex) => { 
                                console.log(`[PublicViewer-Debug] Rendering dish cell for day index ${dayIndex}, DayData:`, dayData);
                              // If dayData is null, render an empty cell
                              if (!dayData) {
                                return <td key={`day-dish-${dayIndex}-course-${courseIndex}`} className="border-0"></td>;
                              }
                              
                              return (
                                <td 
                                  key={`day-dish-${dayIndex}-course-${courseIndex}`} 
                                  className={`p-1 ${getDayCellStyle(dayData, course.name)}`}
                                  style={{
                                    wordWrap: 'break-word',
                                    hyphens: 'auto',
                                    minHeight: '2.5rem'
                                  }}
                                >
                                  <div className="text-sm text-center" title={getDishName(dayData, course.name)}>
                                    {getDishName(dayData, course.name)}
                                    
                                    {/* Display tag icons inline if enabled for this school AND this menu type */}
                                    {school?.show_tags && selectedMenuType?.show_tags !== false && getDishTags(dayData, course.name).length > 0 && (
                                      <>
                                        {' '}
                                        {getDishTags(dayData, course.name).map((tag, i) => (
                                          <img 
                                            key={`tag-${i}`} 
                                            src={getTagLogoUrl(tag.logo_path)} 
                                            alt={tag.name || `Tag ${i+1}`}
                                            title={tag.name || `Tag ${i+1}`}
                                            className="h-4 w-4 object-contain inline align-text-bottom mx-0.5"
                                          />
                                        ))}
                                      </>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          );
                        })}
                        
                        {/* Nutrient Row (Conditionally Rendered) */}
                        {school?.show_nutrients && selectedMenuType?.show_nutrients !== false && (
                          <tr>
                            <td className="border-0 text-right italic text-gray-600 pr-2">
                              {t('nutrientutils.nutrients')}
                            </td>
                            {week.map((dayData, dayIndex) => { 
                              // If dayData is null, render an empty cell for nutrients too
                              if (!dayData) {
                                return <td key={`nutrient-day-${dayIndex}`} className="border-0"></td>;
                              }
                              
                              const summary = isWeeklyView ? 
                                getDayNutrientSummary(dayData) : // Get calculated summary for weekly view
                                dayData?.nutrient_summary;       // Use pre-calculated summary for monthly view
                              
                              let content = '-';
                              let cellStyle = 'text-gray-500 bg-gray-100'; // Default for unavailable

                              if (summary?.status === 'complete') {
                                // Updated content string to include unit
                                content = summary.nutrients.map(n => {
                                  const unitString = n.unit ? ` ${n.unit}` : '';
                                  const valueString = n.totalValue !== null ? Math.round(n.totalValue) : '-';
                                  return `${n.name}: ${valueString}${unitString}`;
                                }).join(', ') || t('menu.nutrientDataComplete');
                                cellStyle = 'text-green-700 bg-gray-50';
                              } else if (summary?.status === 'incomplete') {
                                content = t('menu.nutrientDataIncomplete');
                                cellStyle = 'text-yellow-700 italic bg-gray-50';
                              }
                              // 'unavailable' status uses the default content and style

                              return (
                                <td 
                                  key={`nutrient-day-${dayIndex}`} 
                                  className={`py-2 px-3 text-center text-xs border border-gray-200 ${cellStyle}`}
                                  style={{
                                    wordWrap: 'break-word',
                                    hyphens: 'auto'
                                  }}
                                  title={typeof content === 'string' ? content : ''}
                                >
                                  {content}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ---- Mobile Single-Day display ---- */} 
            <div className="w-full block md:hidden"> 
              {(() => {
                const currentDayData = getSelectedDayData();
                if (!currentDayData) {
                  return <p className="text-center italic p-4 text-gray-500">{t('menu.noDataForDay')}</p>;
                }

                const currentIndex = validDays.findIndex(day => day.day === selectedDay);
                const isFirstDay = currentIndex === 0;
                const isLastDay = currentIndex === validDays.length - 1;

                return (
                  <table className="min-w-full w-full border-collapse">
                    <thead>
                      <tr>
                        {/* Header: Course Name Column (empty) - Remove border */}
                        <th className="p-2 w-1/4"></th> 
                        {/* Header: Day Info + Navigation */}
                        <th 
                          className="p-2 text-center font-medium border border-gray-300"
                          style={{ 
                              backgroundColor: !isDayEmpty(currentDayData) ? accentColor : '#555555', 
                              color: !isDayEmpty(currentDayData) ? accentTextColor : '#FFFFFF'
                            }}
                        >
                          <div className="flex items-center justify-between">
                             {/* Previous Day Button */} 
                             <button 
                                onClick={handlePreviousDay} 
                                disabled={isWeeklyView 
                                  ? selectedDay <= 0 
                                  : validDays.findIndex(day => day.day === selectedDay) <= 0} 
                                className="p-1 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:text-black transition-colors"
                                aria-label={t('menu.previousDay')}
                             >
                                <FaChevronLeft />
                             </button>
                             {/* Day Info */}
                             <div className="flex flex-col items-center">
                               <span className="text-xl md:text-base font-semibold">{formatDayHeader(currentDayData)}</span>
                               {isWeeklyView && currentDayData?.specialName && (
                                 <span className="text-md md:text-sm font-normal italic">{currentDayData.specialName}</span>
                               )}
                               {!isWeeklyView && currentDayData?.specialName && (
                                 <span className="text-md md:text-sm font-normal italic">{currentDayData.specialName}</span>
                               )} 
                             </div>
                            {/* Next Day Button */} 
                             <button 
                                onClick={handleNextDay} 
                                disabled={isWeeklyView 
                                  ? selectedDay >= validDays.length - 1
                                  : isLastDay} 
                                className="p-1 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:text-black transition-colors"
                                aria-label={t('menu.nextDay')}
                             >
                                <FaChevronRight />
                             </button>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Course Rows */}
                      {allCourses.map((course, courseIndex) => {
                        const courseName = isWeeklyView ? course.name : course.name; // Always use course.name
                        return (
                        <tr key={`mobile-course-${courseIndex}`} className="text-center">
                          {/* Course Name - Ensure no borders */}
                          <td className="p-2 text-right italic text-gray-600 border-0">
                               {courseName}
                          </td>
                          {/* Dish Name - Keep right/top/bottom border? Check appearance */}
                          <td 
                               className={`p-2 border-t border-r border-b border-gray-300 ${getDayCellStyle(currentDayData, courseName)}`} 
                             style={{ minHeight: '2.5rem' }} // Ensure cells have some height
                          >
                              <div className="text-lg md:text-base text-center" title={getDishName(currentDayData, courseName)}>
                                {getDishName(currentDayData, courseName)}
                                
                                {/* Display tag icons inline if enabled for this school AND this menu type */}
                                {school?.show_tags && selectedMenuType?.show_tags !== false && getDishTags(currentDayData, courseName).length > 0 && (
                                  <>
                                    {' '}
                                    {getDishTags(currentDayData, courseName).map((tag, i) => (
                                      <img 
                                        key={`tag-${i}`} 
                                        src={getTagLogoUrl(tag.logo_path)} 
                                        alt={tag.name || `Tag ${i+1}`}
                                        title={tag.name || `Tag ${i+1}`}
                                        className="h-5 w-5 object-contain inline align-text-bottom mx-0.5"
                                      />
                                    ))}
                                  </>
                                )}
                              </div>
                          </td>
                        </tr>
                        );
                      })}
                      
                      {/* Nutrient Row (Conditionally Rendered) */}
                       {school?.show_nutrients && selectedMenuType?.show_nutrients !== false && (
                          <tr>
                            {/* Nutrient Label - Remove borders */}
                            <td className="p-2 text-right italic text-gray-600 border-0">
                              {t('nutrientutils.nutrients')}
                            </td>
                            {(() => {
                               const summary = isWeeklyView 
                                 ? getDayNutrientSummary(currentDayData) // Use our new function for weekly view
                                 : currentDayData?.nutrient_summary;      // Use existing for monthly view
                                 
                               let content = '-';
                               let cellStyle = 'text-gray-500 bg-gray-100';

                               if (summary?.status === 'complete') {
                                content = summary.nutrients.map(n => {
                                    const unitString = n.unit ? ` ${n.unit}` : '';
                                    const valueString = n.totalValue !== null ? Math.round(n.totalValue) : '-';
                                    return `${n.name}: ${valueString}${unitString}`;
                                  }).join(', ') || t('menu.nutrientDataComplete');
                                  cellStyle = 'text-green-700 bg-gray-50';
                               } else if (summary?.status === 'incomplete') {
                                  content = t('menu.nutrientDataIncomplete');
                                  cellStyle = 'text-yellow-700 italic bg-gray-50';
                               }
                                
                               return (
                                /* Nutrient Data Cell - Restore borders */
                                <td 
                                  className={`p-2 text-center text-sm md:text-xs border border-gray-300 ${cellStyle}`} 
                                  title={typeof content === 'string' ? content : ''}
                                >
                                  {content}
                                </td>
                               );
                            })()}
                          </tr>
                        )}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Custom content */}
            {menu && menu.customContent && (
              <div 
                className="mt-8 p-4 border border-gray-200 rounded bg-gray-50 text-black public-custom-content"
                dangerouslySetInnerHTML={{ __html: menu.customContent }}
              />
            )}

            {/* Tag Legend - only shown when tags are enabled */}
            {school?.show_tags && selectedMenuType?.show_tags !== false && menu?.tags && menu.tags.length > 0 && (
              <div className="mt-8 p-4 border border-gray-200 rounded bg-gray-50">
                <h3 className="text-lg font-semibold mb-2">{t('tags.legend', 'Tag Legend')}</h3>
                <div className="flex flex-wrap gap-3">
                  {menu.tags.map((tag, index) => (
                    <div key={`legend-${index}`} className="flex items-center">
                      <img 
                        src={getTagLogoUrl(tag.logo_path)}
                        alt={tag.name || `Tag ${index+1}`}
                        className="h-6 w-6 object-contain mr-1"
                      />
                      <span className="text-sm">{tag.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      
      <footer 
        className="p-4 text-center text-sm" 
        style={{ backgroundColor: headerBgColor, color: headerTextColor }}
      >
        {t('menu.footer')}
      </footer>
    </div>
  );
};

export default PublicMenuViewer; 